import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../../sharedComponents/Notifications';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';
import { authorAuthService, Category } from '../services/authorAuth';
import { useInitializeSubmissionPage } from '../hooks/useInitializeSubmissionPage';

const CreateManuscript: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states with structured author approach
  const [formData, setFormData] = useState({
    id: '00000000-0000-0000-0000-000000000000',
    title: '',
    // BookAuthors is the new structured authors array. Authors string kept empty when using BookAuthors.
    bookAuthors: [] as { id: string; name: string; affiliateInstitution: string; isLeadAuthor: boolean }[],
    otherAuthors: '',
    numberOfPages: '',
    positionOfJournalRepresentative: '',
    journalProfessionalAssociation: '',
    applicationType: 1, // 1 = Book, 2 = Journal
    categoryId: 0,
    abstract: '', // Sent to API as standard backend property field
    issue: '',
    publicationUrl: '',
    volume: '',
    uploadedFile: null as File | null,
    fileName: '',
    fileType: '',
    fileSize: 0
  });

  useInitializeSubmissionPage(setCategories, setIsLoading, '/');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'categoryId' || name === 'applicationType' ? Number(value) : value
    }));
  };

  // BookAuthors helpers and add-author mini-form state
  const [showAddAuthorForm, setShowAddAuthorForm] = useState(false);
  const [authorForm, setAuthorForm] = useState({ name: '', affiliateInstitution: '', isLeadAuthor: false });

  const handleAuthorFormChange = (field: string, value: string | boolean) => {
    setAuthorForm(prev => ({ ...prev, [field]: value } as any));
  };

  const generateId = () => Date.now().toString() + Math.random().toString(36).slice(2);
  const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
  const isGuid = (s: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(s);

  const submitAddAuthor = () => {
    if (!authorForm.name.trim()) return;

    setFormData(prev => {
      const existingLead = prev.bookAuthors.find(a => a.isLeadAuthor);
      const shouldBeLead = authorForm.isLeadAuthor || !existingLead;
      const newAuthor = {
        id: generateId(),
        name: authorForm.name.trim(),
        affiliateInstitution: authorForm.affiliateInstitution.trim(),
        isLeadAuthor: shouldBeLead
      };
      // If the new author is set to lead, unset lead on others
      const updated = prev.bookAuthors.map(a => ({ ...a, isLeadAuthor: shouldBeLead ? false : a.isLeadAuthor }));
      return { ...prev, bookAuthors: [...updated, newAuthor] };
    });

    setAuthorForm({ name: '', affiliateInstitution: '', isLeadAuthor: false });
    setShowAddAuthorForm(false);
  };

  const removeBookAuthor = (id: string) => {
    setFormData(prev => ({ ...prev, bookAuthors: prev.bookAuthors.filter(a => a.id !== id) }));
  };

  const removeOtherAuthorsField = () => {
    setFormData(prev => ({ ...prev, otherAuthors: '' }));
  };

  const toggleLeadForAuthor = (id: string) => {
    setFormData(prev => ({
      ...prev,
      bookAuthors: prev.bookAuthors.map(a => ({ ...a, isLeadAuthor: a.id === id }))
    }));
  };

  const getCompiledAuthorsString = (): string => {
    const segments: string[] = [];
    formData.bookAuthors.forEach(a => {
      if (a.isLeadAuthor) segments.push(`${a.name} (Lead Author)`);
      else segments.push(a.name);
    });
    if (formData.otherAuthors.trim()) {
      segments.push(`Others: ${formData.otherAuthors.trim()}`);
    }
    return segments.join(', ');
  };

  // Evaluation steps validation properties
  const isStep1Valid =
    formData.title.trim() &&
    formData.categoryId !== 0 &&
    formData.abstract.trim() &&
    formData.bookAuthors.length > 0 &&
    formData.bookAuthors.filter(a => a.isLeadAuthor).length === 1;

  const isStep2Valid = formData.uploadedFile !== null;

  const steps = [
    { number: 1, label: 'Manuscript Info' },
    { number: 2, label: 'Upload File' },
    { number: 3, label: 'Review & Submit' }
  ];

  const handleSubmit = async () => {
    if (!formData.uploadedFile) return;

    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append('Id', formData.id);
      data.append('Title', formData.title);
        data.append('Authors', '');
        formData.bookAuthors.forEach((author, index) => {
          const idToSend = isGuid(author.id) ? author.id : ZERO_GUID;
          data.append(`BookAuthors[${index}].Id`, idToSend);
          data.append(`BookAuthors[${index}].IsLeadAuthor`, author.isLeadAuthor ? 'true' : 'false');
          data.append(`BookAuthors[${index}].Name`, author.name);
          data.append(`BookAuthors[${index}].AffiliateInstitution`, author.affiliateInstitution || '');
        });
      const pages = parseInt((formData.numberOfPages as any) || '', 10) || 0;
      data.append('NumberOfPages', pages.toString());
      data.append('ISBNNo', ''); 
      data.append('Edition', ''); 
      data.append('Keywords', ''); 
      data.append('ApplicationType', formData.applicationType.toString());
      data.append('CategoryId', formData.categoryId.toString());
      data.append('Abstract', formData.abstract);
      
      if (formData.applicationType === 2) {
        data.append('Issue', formData.issue);
        data.append('PublicationUrl', formData.publicationUrl);
        data.append('Volume', formData.volume);
        data.append('PositionOfJournalRepresentative', formData.positionOfJournalRepresentative);
        data.append('JournalProfessionalAssociation', formData.journalProfessionalAssociation);
      }
      data.append('ManuscriptFile', formData.uploadedFile);

      const response = await authorAuthService.createUpdateSubmission(data);

      if (response.isSuccess) {
        showNotification('success', {
          title: 'Submission Successful!',
          message: response.message || 'Your manuscript has been submitted for TETFund TAG approval.',
          confirmButtonText: 'OK',
          onConfirm: () => navigate("/")
        });
      } else {
        showNotification('error', {
          title: 'Submission Failed',
          message: response.message || 'There was an error submitting your manuscript. Please try again.'
        });
      }
    } catch (error: any) {
      showNotification('error', {
        title: 'Submission Failed',
        message: error.response?.data?.message || error.message || 'There was an error submitting your manuscript. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-[#088A61] animate-spin" />
          <p className="text-gray-600 font-medium">Loading ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="mb-0">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm transition-colors"
        >
          <i className="bi bi-arrow-left"></i>
          Back to Dashboard
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Submission of manuscript
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" style={{ zIndex: 1 }}></div>

          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center relative" style={{ zIndex: 2 }}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${step.number <= activeStep
                  ? 'bg-[#088A61] text-white'
                  : 'bg-gray-100 text-gray-400'
                  }`}
              >
                {step.number}
              </div>
              <span
                className={`mt-2 text-xs font-medium ${step.number <= activeStep ? 'text-[#088A61]' : 'text-gray-500'
                  }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeStep === 1 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Manuscript Information
              </h2>
              <p className="text-sm text-gray-600">
                Provide details about your manuscript
              </p>
            </div>

            <form className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                  Manuscript Title *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="title"
                  name="title"
                  placeholder="Enter the title of your manuscript"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label htmlFor="categoryId" className="block text-sm font-semibold text-gray-700 mb-2">
                  Thematic Area *
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all appearance-none bg-white font-medium"
                  id="categoryId"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  style={{
                    color: formData.categoryId !== 0 ? '#111827' : '#6B7280'
                  }}
                >
                  <option value={0}>Select a thematic area</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* DYNAMIC AUTHORS SECTION */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Authors Registry</h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddAuthorForm(true)}
                      className="px-3 py-1.5 bg-[#088A61] text-white text-xs font-semibold rounded-md hover:bg-[#066e4d] flex items-center gap-1 transition-all"
                    >
                      <Plus size={14} /> Add Author
                    </button>
                    <span className="text-xs text-gray-500">Exactly one Lead Author required</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {formData.bookAuthors.length === 0 && (
                    <p className="text-sm text-gray-500">No authors added yet.</p>
                  )}

                  {formData.bookAuthors.map((a) => (
                    <div key={a.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                      <div>
                        <p className="font-semibold">
                          {a.name}
                          {a.isLeadAuthor && (
                            <span className="text-xs text-white bg-[#088A61] px-2 py-0.5 rounded ml-2">Lead</span>
                          )}
                        </p>
                        {a.affiliateInstitution && <p className="text-xs text-gray-500">{a.affiliateInstitution}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {!a.isLeadAuthor && (
                          <button type="button" onClick={() => toggleLeadForAuthor(a.id)} className="text-sm text-[#088A61] hover:underline">Set Lead</button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeBookAuthor(a.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg border border-red-100 bg-white"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {showAddAuthorForm && (
                  <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Name *</label>
                      <input
                        type="text"
                        value={authorForm.name}
                        onChange={(e) => handleAuthorFormChange('name', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Affiliate Institution</label>
                      <input
                        type="text"
                        value={authorForm.affiliateInstitution}
                        onChange={(e) => handleAuthorFormChange('affiliateInstitution', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={authorForm.isLeadAuthor}
                          onChange={(e) => handleAuthorFormChange('isLeadAuthor', e.target.checked)}
                        />
                        <span className="text-sm">Mark as Lead Author</span>
                      </label>
                      <div className="flex-1"></div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddAuthorForm(false);
                          setAuthorForm({ name: '', affiliateInstitution: '', isLeadAuthor: false });
                        }}
                        className="px-3 py-1 rounded bg-gray-100"
                      >
                        Cancel
                      </button>
                      <button type="button" onClick={submitAddAuthor} className="px-3 py-1 bg-[#088A61] text-white rounded">Add</button>
                    </div>
                  </div>
                )}

                {/* Overflow "Others" Field blocks */}
                {formData.otherAuthors !== '' && (
                  <div className="pt-2 animate-in fade-in duration-300">
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="otherAuthors" className="block text-xs font-semibold text-gray-600">
                        Others (Other Contributors)
                      </label>
                      <button
                        type="button"
                        onClick={removeOtherAuthorsField}
                        className="text-xs text-red-500 hover:underline flex items-center gap-0.5"
                      >
                        Remove Others
                      </button>
                    </div>
                    <textarea
                      id="otherAuthors"
                      name="otherAuthors"
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none text-sm resize-y"
                      rows={2}
                      placeholder="Enter remaining authors separated by commas..."
                      value={formData.otherAuthors === ' ' ? '' : formData.otherAuthors}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="numberOfPages" className="block text-sm font-semibold text-gray-700 mb-2">Number of Pages</label>
                  <input
                    type="number"
                    min={1}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="numberOfPages"
                    name="numberOfPages"
                    placeholder="Enter number of pages"
                    value={formData.numberOfPages as any}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
                    }}
                  />
                </div>
              </div>

              {formData.applicationType === 2 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="volume" className="block text-sm font-semibold text-gray-700 mb-2">
                        Volume
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                        id="volume"
                        name="volume"
                        placeholder="Enter volume"
                        value={formData.volume}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="issue" className="block text-sm font-semibold text-gray-700 mb-2">
                        Issue
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                        id="issue"
                        name="issue"
                        placeholder="Enter issue"
                        value={formData.issue}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label htmlFor="publicationUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                        Publication URL
                      </label>
                      <input
                        type="url"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                        id="publicationUrl"
                        name="publicationUrl"
                        placeholder="https://example.com/publication"
                        value={formData.publicationUrl}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="positionOfJournalRepresentative" className="block text-sm font-semibold text-gray-700 mb-2">
                        Position of Journal Representative *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                        id="positionOfJournalRepresentative"
                        name="positionOfJournalRepresentative"
                        placeholder="e.g. Editor-in-Chief"
                        value={formData.positionOfJournalRepresentative}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="journalProfessionalAssociation" className="block text-sm font-semibold text-gray-700 mb-2">
                        Journal Professional Association *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                        id="journalProfessionalAssociation"
                        name="journalProfessionalAssociation"
                        placeholder="Enter professional association"
                        value={formData.journalProfessionalAssociation}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="abstract" className="block text-sm font-semibold text-gray-700 mb-2">
                  {formData.applicationType === 1 ? 'Preface *' : 'Abstract *'}
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all resize-y"
                  id="abstract"
                  name="abstract"
                  rows={6}
                  placeholder={formData.applicationType === 1 ? "Provide a brief preface of your book manuscript" : "Provide a brief abstract of your journal manuscript"}
                  value={formData.abstract}
                  onChange={handleInputChange}
                ></textarea>
                <div className="mt-2 text-sm text-gray-500">
                  {formData.abstract.length} characters
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className={`px-8 py-3 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${isStep1Valid
                    ? 'bg-gray-800 hover:bg-black text-white cursor-pointer active:scale-95'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  disabled={!isStep1Valid}
                  onClick={() => setActiveStep(2)}
                >
                  Continue to Upload
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            </form>
          </div>
        )}

        {activeStep === 2 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Upload Manuscript
              </h2>
              <p className="text-sm text-gray-600">
                Upload your manuscript file (.doc, .docx)
              </p>
            </div>

            <div
              className="border-2 border-dashed border-gray-300 rounded-2xl p-16 text-center mb-8 cursor-pointer hover:border-[#088A61] hover:bg-[#088A61]/5 transition-all group"
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                type="file"
                id="file-input"
                className="hidden"
                accept=".doc,.docx"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    const fileExtension = file.name.split('.').pop()?.toLowerCase();
                    const isDoc = fileExtension === 'doc' || fileExtension === 'docx';
                    const maxSize = 20 * 1024 * 1024;

                    if (!isDoc) {
                      showNotification('error', {
                        title: 'Invalid File Type',
                        message: 'Please upload your manuscript in DOC or DOCX format only.'
                      });
                      e.target.value = '';
                      return;
                    }

                    if (file.size > maxSize) {
                      showNotification('error', {
                        title: 'File Too Large',
                        message: 'The maximum file size allowed is 20MB.'
                      });
                      e.target.value = '';
                      return;
                    }

                    setFormData(prev => ({
                      ...prev,
                      uploadedFile: file,
                      fileType: fileExtension || 'doc',
                      fileName: file.name,
                      fileSize: file.size
                    }));
                  }
                }}
              />

              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-[#088A61]/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="bi bi-cloud-arrow-up text-3xl text-[#088A61]"></i>
                </div>
              </div>
              <p className="text-gray-900 font-bold mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-gray-500">
                DOC/DOCX only (max. 20MB)
              </p>
            </div>

            {formData.uploadedFile && (
              <div className="bg-[#088A61]/5 rounded-xl p-5 mb-8 border border-[#088A61]/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-500 text-white shadow-lg">
                      <i className="bi bi-file-earmark-word text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {formData.fileName}
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {formData.fileType?.toUpperCase()} • {(formData.fileSize / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        uploadedFile: null,
                        fileName: '',
                        fileType: '',
                        fileSize: 0
                      }));
                      const fileInput = document.getElementById('file-input') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-red-200 text-red-500 hover:bg-red-600 hover:text-white transition-all shadow-sm"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center bg-gray-50 -mx-8 -mb-8 p-8 rounded-b-lg border-t border-gray-100">
              <button
                type="button"
                className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-white transition-all flex items-center gap-2"
                onClick={() => setActiveStep(1)}
              >
                <i className="bi bi-arrow-left"></i>
                Back
              </button>
              <button
                type="button"
                className={`px-8 py-3 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${isStep2Valid
                  ? 'bg-gray-800 hover:bg-black text-white cursor-pointer active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                disabled={!isStep2Valid}
                onClick={() => setActiveStep(3)}
              >
                Review Submission
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {activeStep === 3 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Review & Submit
              </h2>
              <p className="text-sm text-gray-600">
                Please review your submission before submitting
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Manuscript Title</label>
                    <p className="text-lg font-bold text-gray-900 leading-tight">
                      {formData.title}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Compiled Authors List</label>
                    <p className="text-sm font-bold text-gray-900 bg-white p-3 rounded-xl border border-gray-200">
                      {getCompiledAuthorsString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Application Type</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.applicationType === 1 ? 'Books' : 'Journal'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Thematic Area</label>
                    <p className="text-sm font-bold text-gray-900">
                      {categories.find(c => c.id === formData.categoryId)?.name}
                    </p>
                  </div>

                  {formData.applicationType === 2 && (
                    <>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Volume</label>
                        <p className="text-sm font-bold text-gray-900">
                          {formData.volume || <span className="text-gray-400 italic font-medium">None provided</span>}
                        </p>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Issue</label>
                        <p className="text-sm font-bold text-gray-900">
                          {formData.issue || <span className="text-gray-400 italic font-medium">None provided</span>}
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Publication URL</label>
                        <p className="text-sm font-bold text-gray-900">
                          {formData.publicationUrl || <span className="text-gray-400 italic font-medium">None provided</span>}
                        </p>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Position of Representative</label>
                        <p className="text-sm font-bold text-gray-900">
                          {formData.positionOfJournalRepresentative || <span className="text-gray-400 italic font-medium">None provided</span>}
                        </p>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Professional Association</label>
                        <p className="text-sm font-bold text-gray-900">
                          {formData.journalProfessionalAssociation || <span className="text-gray-400 italic font-medium">None provided</span>}
                        </p>
                      </div>
                    </>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      {formData.applicationType === 1 ? 'Preface' : 'Abstract'}
                    </label>
                    <p className="text-sm text-gray-600 leading-relaxed italic whitespace-pre-line">
                      {formData.abstract}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Uploaded Manuscript</label>
                    <div className="flex items-center gap-3 mt-2 bg-white p-3 rounded-xl border border-gray-100">
                      <i className="bi bi-file-earmark-word text-blue-500 text-xl"></i>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{formData.fileName}</p>
                        <p className="text-[10px] font-medium text-gray-400 uppercase">{(formData.fileSize / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center bg-gray-50 -mx-8 -mb-8 p-8 rounded-b-lg border-t border-gray-100">
              <button
                type="button"
                className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-white transition-all flex items-center gap-2"
                onClick={() => setActiveStep(2)}
              >
                <i className="bi bi-arrow-left"></i>
                Modify Upload
              </button>
              <button
                type="button"
                className={`px-10 py-3 rounded-lg text-sm font-extrabold transition-all inline-flex items-center gap-3 shadow-lg ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#088A61] hover:bg-[#066e4d] text-white active:scale-95'
                  }`}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Manuscript
                    <i className="bi bi-send-fill"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateManuscript;